import { Form, theme, App } from "antd";
import { useCallback, useEffect, useState, useRef, useContext } from "react";
import { useIntegrationMixpanelEvent } from "./useMixpanel";
import { useMutation, useQuery } from "react-query";
import { GET_PAID_STATUS, GET_PAID_STEP, GetPaidRawFormData } from "types/get_paid";
import { getGetPaidInitialData, postGetPaidForm } from "api/get_paid";
import { FormInstance } from "antd/lib";
import { AxiosError } from "axios";
import { GetPaidFormContext } from "contexts/get_paid";
import GetPaidActivationModal from "components/layout/GetPaidActivationModal";
import { getPaidBannerDetails, patchPaidBannerDismissStatus } from "api/commercial_document";
import { CommercialDocumentName } from "types/CommercialDocument";
import { CloseCircleFilled } from "@ant-design/icons";

export const useStepValidation = ({
  handleShouldGoNext,
  getPaidStatus,
}: {
  handleShouldGoNext: (shouldGoNext: boolean) => void;
  getPaidStatus?: GET_PAID_STATUS;
}) => {
  const form = Form.useFormInstance<GetPaidRawFormData>();
  const allFieldValues = Form.useWatch([], form);

  const validateStepValues = useCallback(async () => {
    try {
      await form.validateFields({ validateOnly: true });
      handleShouldGoNext(true);
    } catch (_error) {
      handleShouldGoNext(false);
    }
  }, [form, handleShouldGoNext]);

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (getPaidStatus === GET_PAID_STATUS.REJECTED && !hasInitialized.current) {
      handleShouldGoNext(true);
      hasInitialized.current = true;
      return;
    }
    const timeoutId = setTimeout(() => {
      void validateStepValues();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [allFieldValues]);
};

export const useGetPaidMixpanelEvent = ({
  eventName,
  fieldGroup,
  isAllowed = () => true,
}: {
  eventName: string;
  fieldGroup: GET_PAID_STEP;
  isAllowed?: () => boolean;
}) => {
  const form = Form.useFormInstance<GetPaidRawFormData>();
  const fieldGroupValues = Form.useWatch([fieldGroup], form);
  const { isMixpanelStepTriggered } = useContext(GetPaidFormContext);
  const [isMixpanelEventTriggered, setIsMixpanelEventTriggered] = useState(
    isMixpanelStepTriggered?.current[fieldGroup],
  );

  const { triggerMixpanelEvent } = useIntegrationMixpanelEvent();

  useEffect(() => {
    const shouldTriggerMixpanelEvent = Object.values(fieldGroupValues || {}).some(
      (value) => value !== undefined && value !== null && value !== "",
    );

    if (shouldTriggerMixpanelEvent && !isMixpanelEventTriggered && isAllowed()) {
      triggerMixpanelEvent(eventName);
      setIsMixpanelEventTriggered(true);
      if (isMixpanelStepTriggered?.current) {
        isMixpanelStepTriggered.current[fieldGroup] = true;
      }
    }
  }, [fieldGroupValues, isMixpanelEventTriggered, triggerMixpanelEvent]);
};

export const useGetPaidInitialData = ({ isOpen }: { isOpen: boolean }) => {
  const { data } = useQuery({
    queryKey: ["getPaidInitialData"],
    queryFn: getGetPaidInitialData,
    staleTime: 1000 * 60 * 60 * 24,
    enabled: isOpen,
  });

  return { data };
};

export const useFormSubmission = ({ form }: { form: FormInstance<GetPaidRawFormData> }) => {
  const formData = useRef<FormData>(new FormData());

  const { mutate, isSuccess, isError, isLoading } = useMutation<any, AxiosError, FormData>({
    mutationKey: ["postGetPaidForm"],
    mutationFn: postGetPaidForm,
  });

  const handleFormSubmission = useCallback(() => {
    const fields = form.getFieldsValue(true) as GetPaidRawFormData;

    const readyFields = {
      ...fields.organizationInfo,
      ...fields.automation,
      ...fields.activation,
    };

    Object.entries(readyFields).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      if (Array.isArray(value) && value[0]) {
        const file = value[0];
        if (file.originFileObj instanceof File) {
          formData.current.append(key, file.originFileObj);
        } else if (file.url) {
          formData.current.append(key, file.url);
        }
      } else if (
        typeof value === "object" &&
        "value" in value &&
        (typeof value.value === "string" || typeof value.value === "number")
      ) {
        formData.current.append(key, String(value.value));
      } else if (typeof value === "string" || typeof value === "number") {
        formData.current.append(key, String(value));
      }
    });

    if (readyFields.has_website === "yes") formData.current.delete("price_list");
    else formData.current.delete("website");

    mutate(formData.current);
  }, [form, mutate]);

  return { handleFormSubmission, isSuccess, isError, isLoading };
};

export const useGetPaidQueries = ({ document_name }: { document_name: CommercialDocumentName }) => {
  const getPaidBannerQuery = useQuery({
    queryKey: ["getPaidBanner"],
    queryFn: getPaidBannerDetails,
    enabled: document_name === CommercialDocumentName.INVOICE,
  });

  const dismissPaidBannerMutation = useMutation({
    mutationKey: ["dismissPaidBanner", document_name],
    mutationFn: patchPaidBannerDismissStatus,
  });

  return { getPaidBanner: getPaidBannerQuery.data, dismissPaidBannerMutation };
};

export const useGetPaidActivationModal = ({
  document_name,
}: {
  document_name: CommercialDocumentName;
}) => {
  const { modal } = App.useApp();
  const { token } = theme.useToken();

  const { getPaidBanner, dismissPaidBannerMutation } = useGetPaidQueries({
    document_name,
  });

  useEffect(() => {
    if (getPaidBanner) {
      const { get_paid_status, banner_dismiss_status, is_get_paid_enabled } = getPaidBanner;
      if (is_get_paid_enabled && !get_paid_status && !banner_dismiss_status) {
        const infoModal = modal.info({});
        infoModal.update({
          title: null,
          icon: null,
          footer: null,
          closable: true,
          closeIcon: (
            <CloseCircleFilled
              style={{
                color: token.colorBgContainer,
                fontSize: token.fontSizeIcon * 2,
              }}
              onClick={() => dismissPaidBannerMutation.mutate()}
            />
          ),
          styles: {
            content: {
              padding: 0,
              width: "35vw",
            },
          },
          content: <GetPaidActivationModal modal={infoModal} />,
        });
      }
    }
  }, [getPaidBanner]);
};
